import { PostgrestResponse } from "@supabase/supabase-js";
import { IRestricoesAlimentares } from "../../../types/tables/IRestricoesAlimentares";
import { BaseMultiTenantService } from "../base/BaseMultiTenantService";

/**
 * Interface para resultado da verificação de restrições
 */
export interface RestricaoResult {
  permitido: boolean;
  restricoes: RestricaoDetalhada[];
  motivo?: string;
}

/**
 * Interface para restrição detalhada
 */
export interface RestricaoDetalhada {
  id: number;
  fk_id_aluno: number;
  fk_id_produto?: number;
  observacoes?: string;
  severidade: "baixa" | "media" | "alta" | "critica";
  ativo: boolean;
}

/**
 * Serviço para gerenciamento de restrições alimentares
 */
export class RestricoesAlimentaresService extends BaseMultiTenantService<IRestricoesAlimentares> {
  constructor() {
    super("restricoes_alimentares", ["observacoes"], "fk_id_organizacao");
  }

  /**
   * Busca restrições ativas
   */
  async getAtivas(
    organizationId: number,
  ): Promise<PostgrestResponse<IRestricoesAlimentares[]>> {
    try {
      const { data, error } = await this.client
        .from(this.tableName)
        .select("*")
        .eq("fk_id_organizacao", organizationId)
        .eq("ativo", true)
        .is("deleted_at", null)
        .order("id");

      if (error) {
        console.error("❌ Erro ao buscar restrições ativas:", error);
        throw new Error(`Erro ao buscar restrições ativas: ${error.message}`);
      }

      console.log("✅ Restrições ativas encontradas:", data?.length || 0);
      return { data: data || [], error } as PostgrestResponse<any[]>;
    } catch (error) {
      console.error("❌ Erro na consulta de restrições ativas:", error);
      throw error;
    }
  }

  /**
   * Busca restrições de um aluno específico filtradas pela organização
   */
  async getByAluno(
    organizationId: number,
    alunoId: number,
  ): Promise<RestricaoDetalhada[]> {
    try {
      console.log("🔍 Buscando restrições do aluno:", {
        organizationId,
        alunoId,
      });

      const { data, error } = await this.client
        .from(this.tableName)
        .select("*")
        .eq("fk_id_aluno", alunoId)
        .eq("fk_id_organizacao", organizationId)
        .eq("ativo", true)
        .is("deleted_at", null)
        .order("id");

      if (error) {
        console.error("❌ Erro ao buscar restrições do aluno:", error);
        throw new Error(`Erro ao buscar restrições do aluno: ${error.message}`);
      }

      const restricoes: RestricaoDetalhada[] = (data || []).map((item) => ({
        id: item.id,
        fk_id_aluno: item.fk_id_aluno,
        fk_id_produto: item.fk_id_produto || undefined,
        observacoes: item.observacoes || undefined,
        severidade: "media", // Default, pode ser melhorado com campo específico
        ativo: item.ativo,
      }));

      console.log("✅ Restrições do aluno encontradas:", restricoes.length);
      return restricoes;
    } catch (error) {
      console.error("❌ Erro na consulta de restrições do aluno:", error);
      throw error;
    }
  }

  /**
   * Verifica se produtos específicos têm restrições para um aluno
   */
  async verificarRestricoesProdutos(
    organizationId: number,
    alunoId: number,
    produtoIds: number[],
  ): Promise<RestricaoResult> {
    try {
      console.log("🛡️ Verificando restrições:", { alunoId, produtoIds });

      if (!produtoIds || produtoIds.length === 0) {
        return {
          permitido: true,
          restricoes: [],
        };
      }

      const restricoesAluno = await this.getByAluno(organizationId, alunoId);

      if (restricoesAluno.length === 0) {
        console.log("✅ Nenhuma restrição encontrada para o aluno");
        return {
          permitido: true,
          restricoes: [],
        };
      }

      // Fail-closed: restrições sem produto vinculado bloqueiam TODOS os produtos
      const restricoesGerais = restricoesAluno.filter((r) => !r.fk_id_produto);
      if (restricoesGerais.length > 0) {
        const motivo = `Aluno possui restrições alimentares gerais: ${restricoesGerais
          .map((r) => r.observacoes || "Restrição não especificada")
          .join(", ")}`;
        console.warn(
          "⚠️ Aluno tem restrições gerais (sem produto vinculado):",
          restricoesGerais,
        );
        return {
          permitido: false,
          restricoes: restricoesGerais,
          motivo,
        };
      }

      // Verificar se algum produto está nas restrições específicas
      const produtosRestringidos = restricoesAluno.filter(
        (restricao) =>
          restricao.fk_id_produto &&
          produtoIds.includes(restricao.fk_id_produto),
      );

      if (produtosRestringidos.length > 0) {
        const motivo = `Produtos com restrições detectados: ${produtosRestringidos
          .map((r) => r.observacoes || `Produto ${r.fk_id_produto}`)
          .join(", ")}`;

        console.warn(
          "⚠️ Produtos com restrições encontrados:",
          produtosRestringidos,
        );

        return {
          permitido: false,
          restricoes: produtosRestringidos,
          motivo,
        };
      }

      console.log("✅ Produtos liberados, sem restrições");
      return {
        permitido: true,
        restricoes: [],
      };
    } catch (error) {
      console.error("❌ Erro ao verificar restrições:", error);

      // Fail-closed: sem validação confiável, a compra deve ser bloqueada.
      return {
        permitido: false,
        restricoes: [],
        motivo:
          "Não foi possível validar restrições alimentares no momento. Tente novamente.",
      };
    }
  }

  /**
   * Busca restrições de uma pessoa específica (método legado)
   */
  async getByPessoa(organizationId: number, pessoaId: number): Promise<any[]> {
    return await this.getByAluno(organizationId, pessoaId);
  }

  /**
   * Busca restrições por severidade
   */
  async getBySeveridade(
    organizationId: number,
    severidade: string,
  ): Promise<PostgrestResponse<IRestricoesAlimentares[]>> {
    try {
      const { data, error } = await this.client
        .from(this.tableName)
        .select("*")
        .eq("fk_id_organizacao", organizationId)
        .eq("ativo", true)
        .is("deleted_at", null)
        .order("id");

      if (error) {
        console.error("❌ Erro ao buscar restrições por severidade:", error);
        throw new Error(
          `Erro ao buscar restrições por severidade: ${error.message}`,
        );
      }

      // Filtrar por severidade se houver campo específico no futuro
      console.log(
        `✅ Restrições encontradas para severidade "${severidade}":`,
        data?.length || 0,
      );
      return { data: data || [], error } as PostgrestResponse<any[]>;
    } catch (error) {
      console.error("❌ Erro na consulta por severidade:", error);
      throw error;
    }
  }
}

export const restricoesAlimentaresService = new RestricoesAlimentaresService();
